import axios from 'axios'
import { delay } from '../../_augmentt/utils/utils'
import { HUB_TASK_WARNINGS } from '../util/hub-task-management/hub.constants'
import { generateHubTaskUrl } from '../util/hub-task-management/hub.utils'
import { TaskStillProcessingError } from '../util/hub-task-management/taskStillProcessing'

const ROOT = process.env.REACT_APP_AUGMENTT_API_URL

export const HUB_TASK_CAP_URL = `${ROOT}/hub/microsoft/cap`
export const HUB_STATUS_URL = `${ROOT}/hub`

export const HUB_TASK_STATUS = {
  SUCCESS: 'SUCCESS',
  FAIL: 'FAILED',
}

/**
 * Create a CA Policy
 *
 * @param {Number} customerId The id of the tenant who will own this policy
 * @param {Number} userId The id of the user requesting this action
 * @param {object} parameters The parameters required for creating a new policy
 * @param {object} parameters.policyBody Contains all of the grant,session,user, and other controls contained in a policy, conforming to Graph API keys/values
 * @param {object} options An optional object to inject params into the axios POST
 * @param {object} options.source Allows you to specify the source of the request. ie. policy-management
 * @returns {object} An Axios HTTP response object identifying whether or not the task was created successfully
 */
export function createConditionalAccessPolicy(customerId, userId, parameters, options = {}) {
  return axios.post(
    `${HUB_TASK_CAP_URL}/create`,
    {
      ...parameters,
    },
    {
      params: {
        customerId,
        userId,
        requiresManagementTaskRefresh: true,
        ...options,
      },
    },
  )
}

/**
 * Delete a CA Policy
 *
 * @param {Number} customerId The id of the tenant who owns this policy
 * @param {Number} userId The id of the user requesting this action
 * @param {object} parameters The parameters required for deleting a policy
 * @param {object} parameters.policyId The unique id of the policy in Microsoft Graph
 * @param {object} options An optional object to inject params into the axios POST
 * @param {object} options.source Allows you to specify the source of the request. ie. policy-management
 * @returns {object} An Axios HTTP response object identifying whether or not the task was created successfully
 */
export function deleteConditionalAccessPolicy(customerId, userId, parameters, options = {}) {
  return axios.post(
    `${HUB_TASK_CAP_URL}/delete`,
    {
      policyId: parameters.policyId,
    },
    {
      params: {
        customerId,
        userId,
        requiresManagementTaskRefresh: true,
        ...options,
      },
    },
  )
}

/**
 * Update a CA Policy
 *
 * @param {Number} customerId The id of the tenant who owns this policy
 * @param {Number} userId The id of the user requesting this action
 * @param {object} parameters The parameters required for updating a policy
 * @param {object} parameters.policyId The unique id of the policy in Microsoft Graph
 * @param {object} parameters.policyBody Contains all of the grant,session,user, and other controls contained in a policy, conforming to Graph API keys/values
 * @param {object} options An optional object to inject params into the axios POST
 * @param {object} options.source Allows you to specify the source of the request. ie. policy-management
 * @returns {object} An Axios HTTP response object identifying whether or not the task was created successfully
 */
export function updateConditionalAccessPolicy(customerId, userId, parameters, options = {}) {
  return axios.post(
    `${HUB_TASK_CAP_URL}/update`,
    {
      ...parameters,
    },
    {
      params: {
        customerId,
        userId,
        requiresManagementTaskRefresh: true,
        ...options,
      },
    },
  )
}

/**
 * Create an Onboarding Task for a new user
 *
 * @param {Number} customerId The id of the customer who will own this user
 * @param {Number} userId The id of the user requesting this action
 * @param {object} parameters The parameters required for creating a new user
 * @param {string} parameters.firstName User's first name
 * @param {string} parameters.lastName User's last name
 * @param {string} parameters.email User's email address
 * @param {string} parameters.domain User's domain
 * @param {object} parameters.passwordInfo Password configuration
 * @param {object} parameters.licenseInfo License assignments
 * @param {object} parameters.m365Groups M365 group memberships
 * @param {object} parameters.securityGroups Security group memberships
 * @param {object} parameters.distributionGroups Distribution group memberships
 * @param {object} parameters.sharedMailboxes Shared mailbox access
 * @param {object} options An optional object to inject params into the axios POST
 * @param {object} options.source Allows you to specify the source of the request
 * @returns {object} An Axios HTTP response object identifying whether or not the task was created successfully
 */
export function createOnboardingTask(customerId, userId, parameters, options = {}) {
  const organizationId = options.organizationId || null

  return axios.post(
    `${ROOT}/hub/microsoft/onboarding/create`,
    {
      payload: parameters, // Wrap parameters in payload object for hub validation
      customerId,
      organizationId,
    },
    {
      params: {
        customerId,
        userId,
        ...options,
      },
    },
  )
}

/**
 * Get the current state of an Integration Hub Task
 *
 * @param {String} taskId The unique id of the hub Task in elasticsearch
 * @returns {object} An object containing the current values of a hub Task
 */
export function getIntegrationHubTask(taskId) {
  return axios.get(`${HUB_STATUS_URL}/${taskId}/` + `status`) // eslint-disable-line no-useless-concat
}

/**
 * Continuously polls the backend for the current state of an Integration Hub Task
 *
 * @param {String} taskId The unique id of the hub Task in elasticsearch
 * @param {object} params Optional values to apply to polling. Defaults empty object if none is provided.
 * @param {?number} params.retries The number of attempts to continue making requests for task status if it isn't completed. Default 10 attempts.
 * @param {?number} params.timeout The amount of time in milliseconds to wait between making subsequent requests. Default 2000 ms
 * @returns {object} An object containing the final values of a hub Task when the promise is resolved or rejected
 */
export const pollTaskStatus = async (taskId, params = {}) => {
  const maxRetry = params?.retries || 10
  const timeout = params?.timeout || 2000
  let attempts = 0

  // eslint-disable-next-line consistent-return
  const poll = async (resolve, reject) => {
    const result = await getIntegrationHubTask(taskId)
    attempts += 1

    if (
      result?.data?._source?.status === HUB_TASK_STATUS.SUCCESS || // eslint-disable-line no-underscore-dangle
      result?.data?._source?.status === HUB_TASK_STATUS.FAIL // eslint-disable-line no-underscore-dangle
    ) {
      return resolve(result)
    }
    if (attempts === maxRetry) {
      return reject(new TaskStillProcessingError())
    }
    setTimeout(poll, timeout, resolve, reject)
  }

  return new Promise(poll)
}

export function createHubTask(parameters) {
  const taskUrl = generateHubTaskUrl(parameters)

  const { actions, customerId, userId, organizationId, source, requiresManagementTaskRefresh } = parameters

  return axios.post(
    taskUrl,

    { payload: actions, customerId, organizationId },
    {
      params: {
        customerId,
        userId,
        source: source || '',
        ...(requiresManagementTaskRefresh !== undefined && {
          requiresManagementTaskRefresh: requiresManagementTaskRefresh.toString(),
        }),
      },
    },
  )
}

export function getHubTask(parameters) {
  const taskUrl = generateHubTaskUrl(parameters)
  const { customerId, userId } = parameters

  return axios.get(taskUrl, {
    params: {
      customerId,
      userId,
    },
  })
}

/**
 * Check for task completion after creating a task
 * Parses and throws errors if the task fails
 * @param {object} taskResponse the response from createHubTask
 */
export const checkTaskCompletion = async (taskResponse) => {
  const { taskId } = taskResponse.data

  // wait 4 seconds before checking the backend so the task can be created
  await delay(4 * 1000)

  if (!taskId) {
    throw new Error('Failed to create task')
  }

  const pollTask = await pollTaskStatus(taskId, {
    retries: 180,
    timeout: 5000,
  })
  const taskResult = pollTask?.data?._source // eslint-disable-line no-underscore-dangle

  if (taskResult.status === HUB_TASK_STATUS.FAIL) {
    let message = 'Failed to perform action'
    try {
      const errors = JSON.parse(taskResult.errors)
      message =
        errors?.[0]?.innerError?.message ||
        errors?.[0]?.message?.Message ||
        errors?.[0]?.message ||
        String(taskResult.errors)
    } catch (e) {
      // ignore
    }
    throw new Error(message)
  }
}

/**
 * Whether a completed task stored the given warning. The task document keeps its warnings as a JSON string.
 * @param {object} task the completed task, as checkTaskCompletion returns it
 * @param {string} warning
 * @returns {boolean}
 */
export const hasTaskWarning = (task, warning) => {
  try {
    const warnings = JSON.parse(task?.warnings ?? '[]')
    return Array.isArray(warnings) && warnings.includes(warning)
  } catch (e) {
    return false
  }
}

/**
 * The result of a delete task, shaped like an axios response so useApiControls exposes it as `data`
 * @param {object} task the completed task
 * @returns {{data: {isStoredCopyStale: boolean}}}
 */
export const toDeleteTaskResult = (task) => ({
  data: { isStoredCopyStale: hasTaskWarning(task, HUB_TASK_WARNINGS.staleStoredCopy) },
})
